import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { usePromptStore } from '@/stores/PromptStore.ts';
import { slugify, truncateString } from '@/utils/RandomUtils.ts';
import { logger } from '@/lib/logger.ts';
import type { SessionId } from '@/types/Session.ts';
import type { VariableConfig } from '@/types/edit/VariableConfig.ts';
import { DefaultVariableConfigs } from '@/constants/DefaultVariableConfigs.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { VariableConfigService } from '@/services/config/VariableConfigService.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import { StepConfigService } from '@/services/config/StepConfigService.ts';
import {
    PROMPT_OUTPUT_VARIABLE_PREFIX,
    PROMPT_USER_INPUT_VARIABLE_PREFIX,
} from '@/constants/VariablePrefixes.ts';
import { useClientStore } from '@/stores/ClientStore.ts';
import { ConfigNotFoundError } from '@/errors/edit/ConfigNotFoundError.ts';
import { InvalidInputError } from '@/errors/edit/InvalidInputError.ts';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { Base64 } from 'js-base64';

export abstract class VariableService {
    public static setPromptOutputVariable(
        sessionId: SessionId,
        promptConfigId: PromptConfig['id'],
        variableChainHash: string,
        variableValue: VariableConfig['value'],
    ): boolean {
        const promptConfig = PromptConfigService.getPromptConfig(promptConfigId);
        if (!promptConfig) {
            return false;
        }

        const variableKey = VariableConfigService.getPromptOutputVariableKey(promptConfig.name);

        this.setVariable(sessionId, variableKey, variableChainHash, variableValue);

        return true;
    }

    public static setPromptUserInputVariable(
        sessionId: SessionId,
        promptConfigId: PromptConfig['id'],
        variableChainHash: string,
        variableValue: VariableConfig['value'],
    ): boolean {
        const promptConfig = PromptConfigService.getPromptConfig(promptConfigId);
        if (!promptConfig) {
            return false;
        }

        const variableKey = VariableConfigService.getPromptUserInputVariableKey(promptConfig.name);

        this.setVariable(sessionId, variableKey, variableChainHash, variableValue);

        return true;
    }

    private static setVariable(
        sessionId: SessionId,
        key: VariableConfig['key'],
        variableChainHash: string,
        value: VariableConfig['value'],
    ): void {
        const clientStore = useClientStore();

        if (clientStore.sessionVariables[sessionId] === undefined) {
            clientStore.sessionVariables[sessionId] = {};
        }

        if (clientStore.sessionVariables[sessionId][variableChainHash] === undefined) {
            clientStore.sessionVariables[sessionId][variableChainHash] = {};
        }

        clientStore.sessionVariables[sessionId][variableChainHash][key] = value;

        logger.debug(
            `Session variable set in session with id ${sessionId} (hash: ${variableChainHash}): "${key}" = "${truncateString(String(value), 60)}"`,
        );
    }

    public static updateDefaultVariableValue(
        variableConfigId: VariableConfig['id'],
        value: VariableConfig['value'],
    ): void {
        const variableConfig = DefaultVariableConfigs[variableConfigId];
        if (!variableConfig) {
            throw new ConfigNotFoundError(
                `Default variable config with id ${variableConfigId} not found`,
            );
        }

        if (variableConfig.type !== 'DEFAULT') {
            throw new InvalidInputError(
                `Variable config with id ${variableConfigId} is not a default variable`,
            );
        }

        if (variableConfig.valueType === 'string') {
            if (typeof value !== 'string') {
                throw new InvalidInputError(
                    `Default variable config with id ${variableConfigId} value is not a string`,
                );
            }

            this.setGlobalVariable(variableConfig.key, value as string);
        }

        if (variableConfig.valueType === 'array') {
            if (!Array.isArray(value)) {
                throw new InvalidInputError(
                    `Default variable config with id ${variableConfigId} value is not an array`,
                );
            }

            this.setGlobalVariable(variableConfig.key, value as string[]);
        }
    }

    private static setGlobalVariable(
        key: VariableConfig['key'],
        value: VariableConfig['value'],
    ): void {
        const settingsStore = useSettingsStore();

        settingsStore.globalVariables[key] = value;

        logger.debug(`Global variable set: "${key}" = "${truncateString(String(value), 60)}"`);
    }

    public static extractVariableKeys(input: string): string[] {
        const variableRegex = /\$[a-zA-Z_][a-zA-Z0-9_]*/g;
        const matches = input.match(variableRegex) || [];

        return Array.from(new Set(matches.map((match) => match.slice(1))));
    }

    public static hydrateVariables = (
        variableKeys: VariableConfig['key'][],
        sessionId: SessionId,
        profileConfigId: ProfileConfig['id'],
    ): Record<string, string | undefined> => {
        const result: Record<string, string | undefined> = {};
        for (const variable of variableKeys) {
            result[variable] = undefined;
        }

        // Default variables
        for (const defaultVariableConfig of Object.values(DefaultVariableConfigs)) {
            if (!(defaultVariableConfig.key in result)) {
                continue;
            }

            result[defaultVariableConfig.key] = Array.isArray(defaultVariableConfig.value)
                ? defaultVariableConfig.value.join(',')
                : defaultVariableConfig.value;
        }

        // Custom variables
        const promptStore = usePromptStore();
        for (const customVariable of Object.values(promptStore.variableConfigs)) {
            if (!(customVariable.key in result)) {
                continue;
            }

            if (!customVariable.value) {
                logger.warn(`Custom variable value for "${customVariable.key}" is not set`);

                continue;
            }

            result[customVariable.key] = Array.isArray(customVariable.value)
                ? customVariable.value.join(',')
                : customVariable.value;
        }

        // Session variables
        const clientStore = useClientStore();
        for (const key of variableKeys) {
            if (
                !key.startsWith(PROMPT_USER_INPUT_VARIABLE_PREFIX) &&
                !key.startsWith(PROMPT_OUTPUT_VARIABLE_PREFIX)
            ) {
                continue;
            }

            const profilePromptConfigs = Object.values(
                PromptConfigService.getAllPromptConfigs(),
            ).filter((p) => p.profileId === profileConfigId);
            const promptConfig = profilePromptConfigs.find((p) => {
                return (
                    slugify(p.name, true).toUpperCase() ===
                    key
                        .replace(PROMPT_USER_INPUT_VARIABLE_PREFIX, '')
                        .replace(PROMPT_OUTPUT_VARIABLE_PREFIX, '')
                );
            });
            if (!promptConfig) {
                continue;
            }

            const variableChainHash = VariableService.calculateVariableChainHash(
                sessionId,
                promptConfig.stepId,
            );

            if (
                clientStore.sessionVariables[sessionId] === undefined ||
                clientStore.sessionVariables[sessionId][variableChainHash] === undefined
            ) {
                logger.warn(`Session variable value for "${key}" is not set`);

                continue;
            }

            const value = clientStore.sessionVariables[sessionId][variableChainHash][key];

            if (!value) {
                logger.warn(`Session variable value for "${key}" is not set`);

                continue;
            }

            result[key] = Array.isArray(value) ? value.join(',') : value;
        }

        // Global variables (override default)
        const settingsStore = useSettingsStore();
        for (const [key, value] of Object.entries(settingsStore.globalVariables)) {
            if (!(key in result)) {
                continue;
            }

            if (!value) {
                logger.warn(`Global variable value for "${key}" is not set`);

                continue;
            }

            result[key] = Array.isArray(value) ? value.join(',') : value;
        }

        return result;
    };

    public static getUnsetVariableKeys(
        hydratedVariables: Record<string, string | undefined>,
    ): VariableConfig['key'][] {
        const result = [];

        for (const [key, value] of Object.entries(hydratedVariables)) {
            if (value === undefined) {
                result.push(key);
            }
        }

        return result;
    }

    public static isPromptReadyToRun(
        hydratedVariables: Record<string, string | undefined>,
    ): boolean {
        const unsetVariableKeys = this.getUnsetVariableKeys(hydratedVariables);

        return unsetVariableKeys.length === 0;
    }

    public static getDefaultVariableValue = (
        variableConfigId: VariableConfig['id'],
    ): VariableConfig['value'] => {
        const variableConfig = DefaultVariableConfigs[variableConfigId];
        if (!variableConfig) {
            throw new ConfigNotFoundError(
                `Default variable config with id ${variableConfigId} not found`,
            );
        }

        const settingsStore = useSettingsStore();
        if (settingsStore.globalVariables[variableConfig.key]) {
            return settingsStore.globalVariables[variableConfig.key];
        }

        return variableConfig.value;
    };

    public static calculateVariableChainHash = (
        sessionId: SessionId,
        stepConfigId: StepConfig['id'],
    ): string => {
        const allStepConfigs = StepConfigService.getAllStepConfigs();
        const stepConfig = allStepConfigs[stepConfigId];
        if (!stepConfig) {
            throw new Error(`Failed to find step config with id: ${stepConfigId}`);
        }

        const lowerProfileStepConfigIds = Object.values(allStepConfigs)
            .filter((s) => {
                return s.profileId === stepConfig.profileId && s.order < stepConfig.order;
            })
            .map((s) => s.id);

        const allPromptConfigs = PromptConfigService.getAllPromptConfigs();
        const lowerProfilePromptConfigs = Object.values(allPromptConfigs).filter((p) => {
            return (
                p.profileId === stepConfig.profileId && lowerProfileStepConfigIds.includes(p.stepId)
            );
        });

        let variableChainHash: string = '_';

        for (const promptConfig of lowerProfilePromptConfigs) {
            const extractedVariableKeys = [
                VariableConfigService.getPromptUserInputVariableKey(promptConfig.name),
            ];
            const hydratedVariables = VariableService.hydrateVariables(
                extractedVariableKeys,
                sessionId,
                stepConfig.profileId,
            );

            const userInputVariables = Object.entries(hydratedVariables)
                .filter(([k]) => k.startsWith(PROMPT_USER_INPUT_VARIABLE_PREFIX))
                .reduce(
                    (acc, [key, value]) => {
                        acc[key] = value;
                        return acc;
                    },
                    {} as Record<string, string | undefined>,
                );

            variableChainHash += Base64.encodeURI(JSON.stringify(userInputVariables));
        }

        return variableChainHash;
    };
}
